import CTNButton from '@ctn/ui/components/ctn-button';
import CTNDropdownButton from '@ctn/ui/components/ctn-dropdown-button';

import type { CustomColumnDef } from '@ctn/ui/types/ctn-table';

export type Action<T> = {
	label: string;
	command?: (data: T) => void;
	width: number;
	hideIf?: (data: T) => boolean;
};

// Detecta qué acciones son necesarias en el dataset actual revisando si alguna fila cumple su condición `hideIf`.
const filterActions = <T extends Record<string, unknown>>(data: T[], actions: Array<Action<T>>) =>
	actions.filter(({ hideIf }) => data.some((data) => (hideIf ? !hideIf(data) : true)));

// Genera la columna visual. Si hay >1 acción activa usa Dropdown; si hay 1, usa Botón.
// En ambos casos vuelve a validar la fila individual para no mostrar acciones vacías.
const makeTemplate = <T extends Record<string, unknown>>(
	actions: Array<Action<T>>,
	hasDropdown: boolean,
): CustomColumnDef<T> => {
	// MODO DROPDOWN (Si hay más de 1 tipo de acción posible en la tabla)
	if (hasDropdown) {
		return {
			accessorKey: 'actions',
			enableSorting: false,
			header: '',
			th: {
				width: 48,
			},
			td: {
				align: 'right',
			},
			responsive: { sizeColumn: 'full' },
			cell({ row }) {
				return (
					<CTNDropdownButton
						variant="ghost"
						mode="secondary"
						size="size-32"
						label="Acciones"
						icon={[{ icon: 'moreVertical', color: 'none', position: 'left' }]}
						items={actions
							.filter(({ hideIf }) => (hideIf ? !hideIf(row.original) : true))
							.map(({ label, command = () => undefined }) => ({
								label,
								onClick: () => command(row.original),
							}))}
					/>
				);
			},
		};
	}

	// MODO BOTÓN ÚNICO (Solo existe 1 tipo de acción en toda la tabla)

	// Calculamos el ancho máximo de entre todas las acciones posibles.
	// Si por algún motivo el array viene vacío, le damos 100 por defecto.
	const columnWidth = actions.length > 0 ? Math.max(...actions.map((action) => action.width)) : 100;

	return {
		accessorKey: 'actions',
		enableSorting: false,
		header: '',
		th: {
			width: columnWidth,
		},
		td: {
			align: 'right',
		},
		responsive: { sizeColumn: 'full' },
		cell({ row }) {
			// Buscamos cuál de las acciones del array es la que aplica a ESTA fila
			const singleAction = actions.find(({ hideIf }) => (hideIf ? !hideIf(row.original) : true));

			if (!singleAction) {
				return null;
			}

			return (
				<CTNButton
					variant="ghost"
					mode="secondary"
					size="size-32"
					label={singleAction.label}
					onClick={() => (singleAction.command ? singleAction.command(row.original) : undefined)}
				/>
			);
		},
	};
};

/** Comprobar si alguna columna tiene mas de una opción, se muestra dropdown */
const getHasDropdown = <T extends Record<string, unknown>>({
	data,
	actions,
}: {
	data: T[];
	actions: Array<Action<T>>;
}) =>
	data.some((row) => {
		const activeActionsInRow = actions.filter(({ hideIf }) => (hideIf ? !hideIf(row) : true));
		return activeActionsInRow.length > 1;
	});

/** Orquestador de acciones para tabla: Calcula las acciones activas, decide la estrategia de visualización (Botón vs Dropdown). */
export const makeActions = <T extends Record<string, unknown>>({
	data,
	actions,
}: {
	data: T[];
	actions: Array<Action<T>>;
}): CustomColumnDef<T> => {
	const hasDropdown = getHasDropdown({ data, actions });

	return makeTemplate(actions, hasDropdown);
};
